import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowUpRight } from "lucide-react";
import { useProducts, useCoaLibrary } from "@/lib/products";
import HeroVialCard from "@/components/HeroVialCard";

const EASE = [0.16, 1, 0.3, 1];
const LINES = [
  { t: "Research-grade peptides.", cls: "text-slate-900" },
  { t: "Verified lots.", cls: "italic font-medium text-sky-700" },
];

export default function Hero() {
  const products = useProducts();
  const coa = useCoaLibrary();
  const sample = products.find((p) => p.id === "abl-bpc-10");
  const verified = coa.filter((r) => r.has_file);
  const pure = verified.filter((r) => r.purity);
  const avg = pure.length ? (pure.reduce((a, r) => a + r.purity, 0) / pure.length).toFixed(2) : null;

  return (
    <section className="relative overflow-hidden border-b border-slate-200 bg-[#FAFAFA]">
      <div className="pointer-events-none absolute inset-0 opacity-[0.5]"
        style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #e2e8f0 1px, transparent 0)", backgroundSize: "32px 32px", maskImage: "linear-gradient(to bottom, black, transparent 85%)" }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-20 sm:pt-20 sm:pb-28 grid lg:grid-cols-[1.1fr_1fr] gap-14 lg:gap-10 items-center">
        <div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}
            className="inline-flex items-center gap-2 border border-amber-300 bg-amber-50 text-amber-700 rounded-full px-3 py-1 mb-8" data-testid="hero-ruo-pill">
            <ShieldCheck className="h-3.5 w-3.5" />
            <span className="font-tech text-[11px] uppercase tracking-[0.16em]">For laboratory research only · Not for human consumption</span>
          </motion.div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.04]" data-testid="hero-headline">
            {LINES.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span className={`block ${l.cls}`} initial={{ y: "110%" }} animate={{ y: 0 }}
                  transition={{ duration: 0.8, delay: 0.15 + i * 0.15, ease: EASE }}>{l.t}</motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5, ease: EASE }}
            className="mt-5 font-tech text-xs sm:text-sm uppercase tracking-[0.18em] text-slate-500" data-testid="hero-manufacturer-line">
            Manufactured by Axis Bio Lab · Distributed by Arias Peptides
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
            className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
            Arias Peptides is a research-supply marketplace. Every vial we list is manufactured by Axis Bio Lab,
            assayed by an independent laboratory, and published with its batch Certificate of Analysis — so qualified
            labs can compare, verify and order with confidence.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
            className="mt-8 flex flex-wrap gap-3">
            <Link to="/catalog" data-testid="hero-cta-catalog"
              className="group relative inline-flex items-center gap-2 bg-slate-900 text-white font-semibold text-sm px-6 py-3 rounded-full overflow-hidden shadow-[0_10px_30px_-12px_rgba(15,23,42,0.6)] transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.97]">
              <span className="absolute inset-0 bg-sky-600 scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 ease-out" />
              <span className="relative z-10">Browse the catalog</span>
              <ArrowUpRight className="relative z-10 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link to="/coa-library" data-testid="hero-cta-coa"
              className="group relative inline-flex items-center gap-2 border border-slate-300 text-slate-800 font-semibold text-sm px-6 py-3 rounded-full overflow-hidden transition-transform duration-200 active:scale-[0.97]">
              <span className="absolute inset-0 bg-slate-900 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
              <span className="relative z-10 group-hover:text-white transition-colors duration-300">COA Library →</span>
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-12 grid grid-cols-3 gap-6 max-w-md border-t border-slate-200 pt-6" data-testid="hero-stats">
            {[[verified.length || "—", "Lots with COA on file"], [avg ? `${avg}%` : "—", "Mean HPLC purity"], ["-20°C", "Cold-chain dispatch"]].map(([v, l]) => (
              <div key={l}>
                <p className="font-tech text-lg sm:text-xl font-semibold text-slate-900">{v}</p>
                <p className="text-xs text-slate-500 mt-1 uppercase tracking-wide">{l}</p>
              </div>
            ))}
          </motion.div>
        </div>
        <HeroVialCard product={sample} />
      </div>
    </section>
  );
}
